import { AfterViewInit, Component, ElementRef, HostListener, Input, NgZone, Renderer2, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AppService, Channel, LINK_TYPES, LinkService, ThemeType } from '@portals/api';
import { BaseComponent, ResponseService, WindowRef } from '@portals/core';
import { AuthState, ChannelsState, RolesState, UsersState } from '@portals/store';
import { NgxQrcodeElementTypes, NgxQrcodeErrorCorrectionLevels } from '@techiediaries/ngx-qrcode';
import { ngxLoadingAnimationTypes } from 'ngx-loading';
import { finalize, tap } from 'rxjs';
import { customAlphabet } from 'nanoid';
import { environment } from '@portals/environments';
import { CookieService } from 'ngx-cookie';
import { DeviceDetectorService } from 'ngx-device-detector';
import { MatDialog } from '@angular/material/dialog';
import { AnimationOptions } from 'ngx-lottie';
import { TranslateService } from '@ngx-translate/core';

@Component({
	selector: 'jotto-app-upload-notice',
	templateUrl: './upload-notice.component.html',
	styleUrls: ['./upload-notice.component.scss'],
})
export class UploadNoticeComponent extends BaseComponent implements AfterViewInit {
	channel = this.responseService.currentPage?.parameters.channel as Channel;
	theme?: ThemeType;
	notification = this.responseService.currentPage?.parameters.notification;
	@ViewChild('appContainer', { static: true }) appContainerRef!: ElementRef;
	@ViewChild('lottieButton', { static: true }) lottieButtonRef!: ElementRef;
	@ViewChild('mobileView', { static: true }) mobileViewRef!: ElementRef;
	@ViewChild('channelContent', { static: true })
	channelContentRef!: ElementRef;

	uuid = customAlphabet('1234567890abcdef', 10)();
	protected readonly LINK_TYPES = LINK_TYPES;
	isDesktop: boolean;
	isMobile: boolean;
	isTablet: boolean;
	elementType = NgxQrcodeElementTypes.URL;
	correctionLevel = NgxQrcodeErrorCorrectionLevels.HIGH;
	formLoading = false;
	loaderConfig = {
		animationType: ngxLoadingAnimationTypes.pulse,
		primaryColour: `var(--portals-theme-primary)`,
		secondaryColour: `var(--portals-theme-accent)`,
	};
	form: FormGroup;
	disabled = true;
	dialogRef: any;
	imageMaxWidth = '720px';
	channelImage = 'assets/images/default.png';
	options: AnimationOptions = {
		path: 'assets/images/lottie/spinner.json',
		autoplay: true,
		loop: true,
	};
	maxHeight = '';
	imageHeight = '';
	currentLanguage = 'en';
	customLocalizationSet = false;
	constructor(
		private app: AppService,
		private route: ActivatedRoute,
		private router: Router,
		private channelState: ChannelsState,
		private formBuilder: FormBuilder,
		public roles: RolesState,
		private auth: AuthState,
		private user: UsersState,
		private cookies: CookieService,
		private renderer: Renderer2,
		private deviceService: DeviceDetectorService,
		protected responseService: ResponseService,
		private zone: NgZone,
		public window: WindowRef,
		public dialog: MatDialog,
		public linkService: LinkService,
		private translate: TranslateService
	) {
		super();
		this.form = this.formBuilder.group({
			first_name: [`web-user`],
			last_name: [this.uuid],
			username: [this.uuid],
			password: [this.uuid],
			confirmed: [true],
			blocked: [false],
			role: [1, Validators.required],
			web: [true],
		});
		window.nativeWindow.resizeTo(300, 600);
		this.imageMaxWidth = this.deviceService.getDeviceInfo().deviceType == 'Mobile' ? window.nativeWindow.innerWidth + 'px' : '720px';
		this.isDesktop = this.deviceService.isDesktop();
		this.isMobile = this.deviceService.isMobile();
		this.isTablet = this.deviceService.isTablet();
		const windowHeight = this.isMobile ? window.nativeWindow.innerHeight : 600;
		this.maxHeight = `${windowHeight - 304}px`;
		this.imageHeight = `${((this.isMobile ? 75 : 95) / 100) * (windowHeight - 304)}px`;
		const systemDefaultLanguage = navigator.language.split('-')[0];
		const supportedLanguages = ['en', 'es'];
		const defaultLanguage = supportedLanguages.includes(systemDefaultLanguage) ? systemDefaultLanguage : 'en';
		translate.setDefaultLang(defaultLanguage);
		if (this.notification && this.notification?.localization) {
			this.currentLanguage = this.notification.localization;
			this.customLocalizationSet = true;
			translate.setDefaultLang(this.notification.localization);
		}
		if (this.channel && this.channel?.localization && !this.customLocalizationSet) {
			this.currentLanguage = this.channel.localization;
			this.customLocalizationSet = true;
			translate.setDefaultLang(this.channel.localization);
		}
	}

	ngAfterViewInit() {
		const portrait = window.matchMedia('(orientation: portrait)');
		if (this.notification && this.notification.links && this.notification.links.theme) {
			this.theme = this.notification.links.theme;
		} else {
			if (this.channel.links && this.channel.links.theme) {
				this.theme = this.channel.links.theme;
			}
		}
		if (this.isMobile) {
			portrait.matches ? null : this.renderer.setStyle(this.mobileViewRef.nativeElement, 'max-width', '100%');
			this.renderer.setStyle(this.channelContentRef.nativeElement, 'padding', '0px 28px 0px');
			this.renderer.setStyle(this.appContainerRef.nativeElement, 'background', 'var(--portals-theme-primary)');
			this.renderer.setStyle(this.appContainerRef.nativeElement, 'height', '100vh');
			this.renderer.setStyle(this.appContainerRef.nativeElement, 'max-height', window.innerHeight + 'px');
			this.renderer.setStyle(this.mobileViewRef.nativeElement, 'padding', '20px 0p');
		}

		if (this.isTablet) {
			this.renderer.setStyle(this.mobileViewRef.nativeElement, 'border-radius', '24px');
			this.renderer.setStyle(this.mobileViewRef.nativeElement, 'min-height', '650px');
			this.renderer.setStyle(this.mobileViewRef.nativeElement, 'max-height', '650px');
			this.renderer.setStyle(this.appContainerRef.nativeElement, 'width', '100%');
			if (this.deviceService.device == 'iPhone' && this.deviceService.browser == 'Chrome') {
				this.renderer.setStyle(this.mobileViewRef.nativeElement, 'padding-bottom', '10px');
			}
		}

		if (this.isDesktop) {
			this.renderer.setStyle(this.mobileViewRef.nativeElement, 'min-width', '370px');
			this.renderer.setStyle(this.mobileViewRef.nativeElement, 'max-width', '370px');
			this.renderer.setStyle(this.mobileViewRef.nativeElement, 'min-height', '650px');
			this.renderer.setStyle(this.mobileViewRef.nativeElement, 'max-height', '650px');
		}
	}
	getBackgroundColorClass() {
		if (this.theme && this.theme.background_color) {
			return { background: this.theme.background_color };
		} else {
			return { background: '#491176' };
		}
	}
}
